/**
 * SteamAPIClient module for interfacing with Steam's API
 * ES Module version for web UI - uses backend proxy to avoid CORS
 * Requirements: 2.1, 4.2, 4.3, 4.4
 */

import axios from "axios";

/**
 * Backend proxy server URL
 * The Node.js backend proxies requests to Steam API to avoid CORS issues
 */
const BACKEND_PROXY = (
  import.meta.env.VITE_PROXY_URL || "http://localhost:4005"
).replace(/\/+$/, "");

/**
 * Parses Steam API response and extracts GameData fields
 * @param {Object} apiData - Raw API response data from appdetails endpoint
 * @param {string} steamID - The Steam ID for the game
 * @returns {Object|null} - GameData object or null if parsing fails
 * Requirements: 4.3, 4.4
 */
export function parseAPIResponse(apiData, steamID) {
  try {
    if (!apiData || !apiData.success || !apiData.data) {
      return null;
    }

    const data = apiData.data;

    // Extract required fields
    const gameData = {
      steamID: steamID || data.steam_appid?.toString(),
      source: "steam",
      title: data.name || "",
      imageURL: data.header_image || "",
      genres: (data.genres || []).map((g) => g.description),
      storeURL: `https://store.steampowered.com/app/${steamID || data.steam_appid}/`,
      creator:
        (data.developers && data.developers[0]) ||
        (data.publishers && data.publishers[0]) ||
        "",
      releaseDate: parseReleaseDate(data.release_date),
      isDLC: data.type === "dlc",
    };

    return gameData;
  } catch (error) {
    return null;
  }
}

/**
 * Parses release date from Steam API format
 * @param {Object} releaseDateObj - Release date object from API
 * @returns {Date} - Parsed date or current date if parsing fails
 */
function parseReleaseDate(releaseDateObj) {
  if (!releaseDateObj || !releaseDateObj.date) {
    return new Date();
  }

  try {
    const dateStr = releaseDateObj.date;
    const parsed = new Date(dateStr);

    if (isNaN(parsed.getTime())) {
      return new Date();
    }

    return parsed;
  } catch (error) {
    return new Date();
  }
}

/**
 * Retrieves game details by Steam ID
 * @param {string} steamID - The Steam ID to look up
 * @returns {Promise<Object>} - Promise resolving to GameData object or error
 * Requirements: 4.2, 4.3, 4.4
 */
export async function getGameByID(steamID) {
  try {
    const url = `${BACKEND_PROXY}/api/steam/appdetails`;
    const response = await axios.get(url, {
      params: {
        appids: steamID,
      },
      timeout: 15000,
    });

    if (!response.data || !response.data[steamID]) {
      return {
        success: false,
        error: "Game not found",
      };
    }

    const gameData = parseAPIResponse(response.data[steamID], steamID);

    if (!gameData) {
      return {
        success: false,
        error: "Failed to parse game data",
      };
    }

    return {
      success: true,
      data: gameData,
    };
  } catch (error) {
    // Handle API errors
    if (error.code === "ECONNABORTED") {
      return {
        success: false,
        error: "Request timeout - Steam API is taking too long to respond",
      };
    }

    if (error.response) {
      // API returned an error response
      return {
        success: false,
        error: `Steam API error: ${error.response.status} ${error.response.statusText}`,
      };
    }

    if (error.request) {
      // Request was made but no response received
      return {
        success: false,
        error:
          "Backend proxy is unavailable - make sure server.js is running and VITE_PROXY_URL is set correctly",
      };
    }

    // Other errors
    return {
      success: false,
      error: `Failed to retrieve game data: ${error.message}`,
    };
  }
}

/**
 * Searches for games by query string using Steam Store Search API
 * @param {string} query - Search query string
 * @returns {Promise<Object>} - Promise resolving to SearchResults object or error
 * Requirements: 2.1, 4.2
 */
/**
 * Fetches all owned games for a Steam user
 * @param {string} steamId - Steam64 ID
 * @returns {Promise<Object>} - { success, data: { games, gameCount } } or error
 */
export async function getOwnedGames(steamId) {
  try {
    const response = await axios.get(`${BACKEND_PROXY}/api/steam/owned-games/${steamId}`, {
      timeout: 15000,
    });

    const games = response.data?.response?.games || [];
    return {
      success: true,
      data: {
        games: games.map((g) => ({
          appid: g.appid,
          name: g.name,
          playtimeForever: g.playtime_forever || 0, // minutes
          playtime2Weeks: g.playtime_2weeks || 0,
          lastPlayed: g.rtime_last_played ? new Date(g.rtime_last_played * 1000).toISOString() : null,
          imgIconUrl: g.img_icon_url || "",
        })),
        gameCount: response.data?.response?.game_count || games.length,
      },
    };
  } catch (error) {
    if (error.request && !error.response) {
      return { success: false, error: "Backend proxy is unavailable" };
    }
    return { success: false, error: `Steam API error: ${error.message}` };
  }
}

/**
 * Fetches recently played games for a Steam user (last 2 weeks)
 * @param {string} steamId - Steam64 ID
 * @returns {Promise<Object>} - { success, data: { games } } or error
 */
export async function getRecentlyPlayedGames(steamId) {
  try {
    const response = await axios.get(`${BACKEND_PROXY}/api/steam/recently-played/${steamId}`, {
      timeout: 15000,
    });

    const games = response.data?.response?.games || [];
    return {
      success: true,
      data: {
        games: games.map((g) => ({
          appid: g.appid,
          name: g.name,
          playtimeForever: g.playtime_forever || 0,
          playtime2Weeks: g.playtime_2weeks || 0,
        })),
      },
    };
  } catch (error) {
    if (error.request && !error.response) {
      return { success: false, error: "Backend proxy is unavailable" };
    }
    return { success: false, error: `Steam API error: ${error.message}` };
  }
}

/**
 * Fetches achievement stats for a specific game and user
 * @param {string} steamId - Steam64 ID of the user
 * @param {string} appId - Steam app ID of the game
 * @returns {Promise<Object>} - { success, earned, total } or error
 */
export async function getPlayerAchievements(steamId, appId) {
  try {
    const response = await axios.get(`${BACKEND_PROXY}/api/steam/achievements/${steamId}/${appId}`, {
      timeout: 15000,
    });
    return {
      success: true,
      earned: response.data?.earned ?? 0,
      total: response.data?.total ?? 0,
    };
  } catch (error) {
    if (error.request && !error.response) {
      return { success: false, error: "Backend proxy is unavailable" };
    }
    // Many games don't support achievements — treat 400/500 as "no achievements"
    return { success: false, error: error.response?.data?.error || error.message };
  }
}

export async function searchGamesByQuery(query) {
  try {
    // Validate query
    if (!query || typeof query !== "string" || query.trim().length === 0) {
      return {
        success: false,
        error: "Search query cannot be empty",
      };
    }

    // Use Steam Store Search API
    const searchUrl = `${BACKEND_PROXY}/api/steam/storesearch`;
    const searchResponse = await axios.get(searchUrl, {
      params: {
        term: query,
        cc: "US",
      },
      timeout: 15000,
    });

    if (!searchResponse.data || !searchResponse.data.items) {
      return {
        success: false,
        error: "Failed to retrieve search results from Steam",
      };
    }

    // Filter to only include games and DLC
    const gameItems = searchResponse.data.items.filter(
      (item) => item.type === "app" || item.type === "dlc",
    );

    // Limit to first 10 results for performance
    const limitedItems = gameItems.slice(0, 10);

    // Fetch full details for each game to get complete information
    const gameDataPromises = limitedItems.map((item) =>
      getGameByID(item.id.toString()),
    );

    const results = await Promise.all(gameDataPromises);

    // Filter out failed requests
    const successfulResults = results
      .filter((result) => result.success)
      .map((result) => result.data);

    return {
      success: true,
      data: {
        games: successfulResults,
        totalCount: successfulResults.length,
      },
    };
  } catch (error) {
    // Handle API errors
    if (error.code === "ECONNABORTED") {
      return {
        success: false,
        error: "Request timeout - Steam API is taking too long to respond",
      };
    }

    if (error.response) {
      return {
        success: false,
        error: `Steam API error: ${error.response.status} ${error.response.statusText}`,
      };
    }

    if (error.request) {
      return {
        success: false,
        error:
          "Backend proxy is unavailable - make sure server.js is running and VITE_PROXY_URL is set correctly",
      };
    }

    return {
      success: false,
      error: `Failed to search games: ${error.message}`,
    };
  }
}
